import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { useTheme } from '../lib/theme'

// The public marketing surface is the Navo24 light brand; the signed-in app stays dark.
const APP_ROUTE = /^\/(dashboard|admin|login)(\/|$)/
const ADMIN_SCHEDULES = /^\/admin\/schedules(\/|$)/
function useRouteTheme(pathname: string) {
  useEffect(() => {
    const el = document.documentElement
    const stored = localStorage.getItem('theme')
    const t =
      stored === 'dark' || stored === 'light' ? stored : APP_ROUTE.test(pathname) ? 'dark' : 'light'
    el.setAttribute('data-theme', t)
  }, [pathname])
}

function ThemeToggle() {
  const [theme, toggle] = useTheme()
  const dark = theme === 'dark'
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={dark ? 'Light theme' : 'Dark theme'}
      className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-border-strong transition-colors"
    >
      {dark ? (
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
        </svg>
      )}
    </button>
  )
}

// Grouped hover menus (no second click — panels open on hover, first tap on touch, CSS-only via
// group-hover). The hub directories (/carriers, /lanes, /vessels, /storms) were unreachable from
// the old flat header; they carry most of the SEO surface.
interface NavGroupLink {
  to: string
  label: string
  desc: string
}
const MENUS: { label: string; cols: { title: string; links: NavGroupLink[] }[] }[] = [
  {
    label: 'Schedules',
    cols: [
      {
        title: 'Plan',
        links: [
          { to: '/compare', label: 'Compare lanes', desc: 'Reliability-scored sailing choices' },
          { to: '/lanes', label: 'Lane directory', desc: '134 lanes, measured on-time %' },
          {
            to: '/carriers',
            label: 'Carrier directory',
            desc: '42 carriers, observed reliability',
          },
          { to: '/vessels', label: 'Vessel directory', desc: 'Ships, rotations, port calls' },
        ],
      },
      {
        title: 'Live ops',
        links: [
          { to: '/ports', label: 'Port congestion', desc: 'Waiting times, right now' },
          // Parked with the weather surface and its routes. Uncomment all three together with the
          // routes in main.tsx: a menu entry pointing at a route that no longer exists drops the
          // visitor on the home page with no explanation.
          // { to: '/weather', label: 'Storm map', desc: 'Cyclones over live lanes' },
          // { to: '/disruptions', label: 'Disruptions', desc: 'What is slipping, and why' },
          // { to: '/storms', label: 'Storm archive', desc: 'Named storms and their impact' },
        ],
      },
    ],
  },
  {
    label: 'Developers',
    cols: [
      {
        title: 'Build',
        links: [
          { to: '/mcp', label: 'MCP server', desc: 'Sailings as tool calls: demo key, no signup' },
          {
            to: 'https://navo24.com/developers/reference/schedules',
            label: 'API docs',
            desc: 'REST endpoints and shapes',
          },
          { to: '/vs', label: 'Compare tools', desc: 'SchedulesMCP vs alternatives' },
        ],
      },
    ],
  },
]

function NavItemLink({ link }: { link: NavGroupLink }) {
  const cls =
    'block rounded-lg p-3 no-underline transition-colors hover:bg-[var(--surface-1,rgba(0,0,0,0.04))]'
  const inner = (
    <>
      <span className="block text-[0.9rem] font-semibold text-[var(--text-primary)]">
        {link.label}
      </span>
      <span className="mt-0.5 block text-[0.8rem] leading-snug text-[var(--text-secondary)]">
        {link.desc}
      </span>
    </>
  )
  // External hub links (e.g. the Navo24 docs reference) need a plain anchor;
  // react-router's Link only routes in-app paths.
  if (/^https?:\/\//.test(link.to)) {
    return (
      <a href={link.to} className={cls}>
        {inner}
      </a>
    )
  }
  return (
    <Link to={link.to} className={cls}>
      {inner}
    </Link>
  )
}

function NavMenu({ menu }: { menu: (typeof MENUS)[number] }) {
  const wide = menu.cols.length > 1
  return (
    <div className="group relative">
      <button
        type="button"
        aria-haspopup="true"
        className="flex items-center gap-1 text-sm text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
      >
        {menu.label}
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className="mt-0.5 opacity-50 transition-transform group-hover:rotate-180"
        >
          <path
            d="m6 9 6 6 6-6"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div
        className={`pointer-events-none absolute left-1/2 top-full z-40 -translate-x-1/2 pt-3 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 ${wide ? 'w-[34rem]' : 'w-[21rem]'}`}
      >
        <div
          className={`rounded-xl border border-border bg-[var(--surface-0,var(--bg,#fff))] p-2.5 shadow-[0_30px_70px_-30px_rgba(11,12,14,0.35)] ${wide ? 'grid grid-cols-2 gap-1' : 'grid gap-1'}`}
          style={{ background: 'var(--surface-0, var(--bg, #fff))' }}
        >
          {menu.cols.map((col) => (
            <div key={col.title}>
              <p className="px-3 pb-1 pt-2 font-mono text-[0.66rem] uppercase tracking-[0.14em] text-[var(--text-muted)]">
                {col.title}
              </p>
              {col.links.map((l) => (
                <NavItemLink key={l.to} link={l} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function Nav() {
  const { pathname } = useLocation()
  const { session } = useAuth()
  useRouteTheme(pathname)
  return (
    <nav className="flex flex-wrap items-center justify-between gap-y-2 max-w-6xl mx-auto px-6 py-5 border-b border-border">
      <Link to="/" className="flex items-center gap-2 no-underline">
        <svg width="22" height="22" viewBox="0 0 100 100" aria-hidden="true" className="block">
          <circle cx="50" cy="50" r="48" fill="#0c2a5e" />
          <circle cx="50" cy="50" r="40" fill="#12c2c2" />
          <circle cx="50" cy="50" r="32" fill="#35d0c0" />
          <circle cx="50" cy="50" r="24" fill="#1f4fe6" />
          <circle cx="50" cy="50" r="16" fill="#ffc42e" />
          <circle cx="50" cy="50" r="8" fill="#ff7a1f" />
          <circle cx="50" cy="50" r="3" fill="#fff" />
        </svg>
        <span
          className="font-display font-semibold text-xl text-[var(--text-primary)]"
          style={{ letterSpacing: '-0.02em' }}
        >
          schedules<span className="text-signal">mcp</span>
        </span>
      </Link>
      <div className="flex items-center gap-4 sm:gap-6">
        {MENUS.map((m) => (
          <NavMenu key={m.label} menu={m} />
        ))}
        <Link
          to="/pricing"
          className={`text-sm no-underline transition-colors ${
            pathname === '/pricing'
              ? 'text-[var(--text-primary)]'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
          }`}
        >
          Pricing
        </Link>
        {session ? (
          <Link
            to="/dashboard"
            className="text-sm font-medium text-signal no-underline hover:opacity-80"
          >
            Dashboard
          </Link>
        ) : (
          <Link
            to="/login"
            className="text-sm text-[var(--text-secondary)] no-underline hover:text-[var(--text-primary)]"
          >
            Sign in
          </Link>
        )}
        {/* The Schedules admin has its own light/dark switch (its own key); a second one here would
            flip the site theme under it. */}
        {!ADMIN_SCHEDULES.test(pathname) && <ThemeToggle />}
      </div>
    </nav>
  )
}

export function Footer() {
  return (
    <footer className="max-w-6xl mx-auto px-6 py-10 mt-20 border-t border-border text-sm text-[var(--text-muted)]">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span
            className="font-display font-semibold text-base text-[var(--text-primary)]"
            style={{ letterSpacing: '-0.02em' }}
          >
            schedules<span className="text-signal">mcp</span>
          </span>
          <span
            className="font-mono lowercase text-[var(--text-muted)]"
            style={{ fontSize: '10.5px', letterSpacing: '0.08em' }}
          >
            by navo24
          </span>
          <span className="mt-1">
            © {new Date().getFullYear()} SchedulesMCP · a Navo24 product.
          </span>
        </div>
        <div className="flex gap-5">
          <Link
            to="/compare"
            className="no-underline text-[var(--text-secondary)] hover:text-signal"
          >
            Compare
          </Link>
          <Link to="/vs" className="no-underline text-[var(--text-secondary)] hover:text-signal">
            Compare tools
          </Link>
          <Link to="/mcp" className="no-underline text-[var(--text-secondary)] hover:text-signal">
            MCP server
          </Link>
          <a
            href="https://trackingmcp.com"
            className="no-underline text-[var(--text-secondary)] hover:text-signal"
          >
            TrackingMCP
          </a>
          <a
            href="https://freightratesmcp.com"
            className="no-underline text-[var(--text-secondary)] hover:text-signal"
          >
            FreightRatesMCP
          </a>
        </div>
      </div>
      <p className="mt-4 text-xs text-[var(--text-muted)] max-w-2xl">
        Reliability scores are measured from 4,200+ observed port calls across 134 lanes and 42
        carriers, against first-declared schedules. Methodology on the{' '}
        <Link
          to="/compare"
          className="underline underline-offset-2 hover:text-[var(--text-secondary)]"
        >
          Compare page
        </Link>
        .
      </p>
    </footer>
  )
}
